import React, { useState, useRef, useEffect } from "react";
import styles from "./Select.module.scss";
import Arrow from "../../../assets/images/svgs/Arrow";
import { useTranslation } from "react-i18next";

interface Option {
  id: string;
  name: string;
}

interface SelectProps {
  label?: string;
  error?: string;
  options: Option[];
  value: string;
  onChange: (value: { id: string; name: string } | null) => void;
  className?: string;
  loaderRef?: React.RefObject<HTMLDivElement | null>;
  disabled?: boolean;
  loading?: boolean;
}

const Select: React.FC<SelectProps> = ({
  label,
  error,
  options,
  value,
  onChange,
  className = "",
  loaderRef,
  disabled = false,
  loading = false,
}) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation("translation");

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const selectedOption =
    options.find((opt) => opt.id === value) ||
    options.find((opt) => opt.name === value);

  return (
    <div className={className}>
      <div className={`${styles.selectWrapper} ${className}`} ref={wrapperRef}>
        {label && <label className={styles.label}>{label}</label>}
        <div
          className={`${styles.customSelect} ${
            error ? styles.selectError : ""
          } ${open ? styles.selectActive : ""} ${disabled ? styles.disabled : ""} ${loading ? styles.loading : ""}`}
          onClick={() => !disabled && !loading && setOpen((prev) => !prev)}
        >
          <span>
            {loading
              ? t("placeholders.loading") || "Loading..."
              : selectedOption?.name || t("placeholders.select")}
          </span>
          {!loading && (
            <Arrow className={open ? styles.arrowUp : styles.arrowDown} />
          )}
        </div>
        {open && !disabled && !loading && (
          <div className={styles.optionsDropdown}>
            {options?.length === 0 ? (
              <p style={{ padding: 12, textAlign: "center" }}>
                {t("placeholders.no_options")}
              </p>
            ) : (
              options.map((opt, idx) => (
                <div
                  key={idx}
                  className={`${styles.option} ${
                    value === opt.id || value === opt.name ? styles.active : ""
                  }`}
                  onClick={() => {
                    onChange({ id: opt.id, name: opt.name });
                    setOpen(false);
                  }}
                >
                  {opt.name}
                </div>
              ))
            )}
            {loaderRef && (
              <div ref={loaderRef} className={styles.loaderTrigger} />
            )}
          </div>
        )}
      </div>
      {error && <span className={styles.error}>{error}</span>}
    </div>
  );
};

export default Select;
